import React, { useState } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './select';
import { Input } from './input';

export interface SearchableSelectOption {
  value: string;
  label: string;
  [key: string]: unknown;
}

export interface NoneOption {
  value: string;
  label: string;
}

export interface SearchableSelectDropdownProps {
  value: string;
  onValueChange: (value: string) => void;
  options: SearchableSelectOption[];
  placeholder?: string;
  searchPlaceholder?: string;
  /** First option to allow "clear" (e.g. { value: '__none__', label: 'None' }). Do not use empty string. */
  noneOption?: NoneOption;
  /** Option keys to filter on (e.g. ['label', 'pipeNRI']). Defaults to ['label']. */
  filterKeys?: string[];
  disabled?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** When true, component controls its own open state and search term. */
  uncontrolled?: boolean;
  emptyMessage?: string;
  noOptionsMessage?: string;
  triggerClassName?: string;
  contentClassName?: string;
  /** Called when an option is selected (after onValueChange). Use to e.g. clear local search. */
  onSelect?: () => void;
}

export const SearchableSelectDropdown: React.FC<SearchableSelectDropdownProps> = ({
  value,
  onValueChange,
  options,
  placeholder = 'Select...',
  searchPlaceholder = 'Search...',
  noneOption,
  filterKeys = ['label'],
  disabled = false,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  uncontrolled = false,
  emptyMessage = 'No options available',
  noOptionsMessage = 'No results found',
  triggerClassName,
  contentClassName = 'max-h-[300px]',
  onSelect,
}) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const isControlled = !uncontrolled && controlledOnOpenChange != null;
  const open = isControlled ? Boolean(controlledOpen) : internalOpen;
  const setOpen = isControlled ? controlledOnOpenChange! : setInternalOpen;

  const filterOption = (option: SearchableSelectOption): boolean => {
    if (!searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase();
    return filterKeys.some((key) => {
      const v = option[key];
      return typeof v === 'string' && v.toLowerCase().includes(term);
    });
  };

  const filteredOptions = options.filter(filterOption);

  const handleValueChange = (v: string) => {
    onValueChange(v);
    setSearchTerm('');
    setOpen(false);
    onSelect?.();
  };

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) setSearchTerm('');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    e.stopPropagation();
    if (e.key === 'Escape') {
      setOpen(false);
      setSearchTerm('');
    }
  };

  const showNoneOption = noneOption != null;
  const emptyValue = noneOption?.value ?? '';
  const selectValue = value || emptyValue;

  return (
    <Select
      value={selectValue}
      onValueChange={handleValueChange}
      disabled={disabled}
      open={open}
      onOpenChange={handleOpenChange}
    >
      <SelectTrigger className={triggerClassName}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent className={contentClassName}>
        <div className="p-2 border-b border-gray-200 flex-shrink-0">
          <Input
            placeholder={searchPlaceholder}
            value={searchTerm}
            onChange={(e) => {
              e.stopPropagation();
              setSearchTerm(e.target.value);
            }}
            onKeyDown={handleKeyDown}
            onClick={(e) => e.stopPropagation()}
            className="h-8 text-sm"
            autoFocus
          />
        </div>
        <div className="overflow-y-auto max-h-[240px]">
          {showNoneOption && (
            <SelectItem value={noneOption!.value}>{noneOption!.label}</SelectItem>
          )}
          {options.length > 0 ? (
            filteredOptions.length > 0 ? (
              filteredOptions.map((opt) => (
                <SelectItem key={opt.value} value={String(opt.value)}>
                  {opt.label}
                </SelectItem>
              ))
            ) : (
              <SelectItem value="__no_results__" disabled>
                {noOptionsMessage}
              </SelectItem>
            )
          ) : (
            <SelectItem value="__empty__" disabled>
              {emptyMessage}
            </SelectItem>
          )}
        </div>
      </SelectContent>
    </Select>
  );
};
